import classnames from 'classnames';
import React, { forwardRef, useContext } from 'react';
import { Virtuoso } from 'react-virtuoso';
import styled, { ThemeContext, ThemeProvider } from 'styled-components';
import { ListLooks } from '../../../../enums/list.js';
import '../../../../styles/styled-components/alert.js';
import '../../../../styles/styled-components/animations.js';
import { boxShadowLists } from '../../../../styles/styled-components/box-shadow.js';
import '../../../../styles/styled-components/button.js';
import '../../../../themes/alert.js';
import '../../../../themes/button.js';
import '../../../../themes/checkbox.js';
import '../../../../themes/divider.js';
import '../../../../themes/dropdown.js';
import '../../../../themes/fieldset.js';
import '../../../../themes/input.js';
import '../../../../themes/list.js';
import '../../../../themes/listbox.js';
import '../../../../themes/loader.js';
import '../../../../themes/option.js';
import '../../../../themes/popout.js';
import '../../../../themes/switch.js';
import { lightTheme } from '../../../../themes/themes.js';
import '../../../../themes/tooltip.js';
import '../../../../styles/styled-components/font-face.js';
import '../../../../styles/styled-components/global.js';
import '../../../../styles/styled-components/input.js';
import { baseListStyle, getListVirtualizedCss, getListTheme } from '../../../../styles/styled-components/list.js';
import '../../../../styles/styled-components/listitem.js';
import '../../../../styles/styled-components/media-queries.js';
import '../../../../styles/styled-components/reset.js';
import '../../../../styles/styled-components/outline.js';
import '../../../../styles/styled-components/popout.js';
import '../../../../styles/styled-components/screen-reader.js';
import '../../../../styles/styled-components/switch.js';
import '../../../../styles/styled-components/tooltip.js';
import '../../../atoms/icon/font-icon/font-icon.js';
import 'react-transition-group';
import '../../../atoms/alert/inline-alert/inline-alert.js';
import '../../../atoms/alert/standard-alert/standard-alert.js';
import 'boring-avatars';
import '../../../atoms/tooltip/hover-tooltip/hover-tooltip.js';
import 'react-laag';
import '../../../../utilities/color.js';
import 'date-fns';
import '../../../../utilities/locale.js';
import '@brandfolder/utilities';
import '../../../atoms/button/close-button/close-button.js';
import '../../../atoms/button/color-button/color-button.js';
import '../../../../hooks/use-prevent-keyboard-scrolling.js';
import '../../../atoms/button/icon-button/icon-button.js';
import '../../../atoms/button/standard-button/standard-button.js';
import { CircleLoader } from '../../../atoms/loader/circle-loader/circle-loader.js';
import '../../../atoms/loader/shimmer-loader/shimmer-loader.js';
import '../../../atoms/divider/standard-divider/standard-divider.js';
import '../../../atoms/fieldset/standard-fieldset/standard-fieldset.js';
import '../../../atoms/heading/standard-heading/standard-heading.js';
import '../../../atoms/label/standard-label/standard-label.js';
import '../../../atoms/link/button-link/button-link.js';
import '../../../atoms/link/standard-link/standard-link.js';
import '../../../atoms/popout/standard-popout/standard-popout.js';
import '../../../atoms/screen-reader/only-for-screen-reader/only-for-screen-reader.js';
import '../../../atoms/table/standard-table/caption.js';
import '../../../atoms/table/standard-table/tbody.js';
import '../../../atoms/table/standard-table/tr-empty.js';
import '../../../atoms/table/standard-table/td.js';
import '../../../atoms/table/standard-table/tr.js';
import '../../../atoms/table/standard-table/table.js';
import '../../../atoms/table/standard-table/tfoot.js';
import '../../../atoms/table/standard-table/th.js';
import '../../../atoms/table/standard-table/thead.js';
import '../../../atoms/table/dnd-table/dnd-handle-td.js';
import '../../../atoms/table/dnd-table/dnd-table-drag-context.js';
import '../../../atoms/text/help-text/help-text.js';
import '../../../atoms/text/standard-text/standard-text.js';
import { StandardListItem } from '../../listitem/standard-listitem/standard-listitem.js';
import { BaseList } from '../base-list/base-list.js';

const borderWidth = 1;
const height = 250;
const padding = 10;
const UnstyledList = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { className, labels = {}, listItemProps, listItems, loading, virtualized, virtualizedProps, ...otherProps } = props;
    const { loaderLabel } = labels;
    const isVirtualized = virtualized && listItems.length > 10;
    return /*#__PURE__*/ React.createElement(BaseList, {
        ...otherProps,
        className: classnames({
            'bf-list__standard': true,
            'bf-list__standard--virtualized': isVirtualized,
            [`${className}`]: !!className
        }),
        ref: ref
    }, loading && loaderLabel && /*#__PURE__*/ React.createElement(StandardListItem, listItemProps, /*#__PURE__*/ React.createElement(CircleLoader, {
        label: loaderLabel
    })), !loading && isVirtualized && /*#__PURE__*/ React.createElement(Virtuoso, {
        ...virtualizedProps,
        className: classnames({
            'bf-list__virtualized': true,
            [`${virtualizedProps?.className}`]: !!virtualizedProps?.className
        }),
        itemContent: (index)=>{
            return /*#__PURE__*/ React.createElement(StandardListItem, {
                ...listItemProps,
                key: listItems[index].key
            }, listItems[index].children);
        },
        style: {
            height: height - borderWidth * 2
        },
        totalCount: listItems.length
    }), !loading && !isVirtualized && listItems.map((listItem)=>{
        return /*#__PURE__*/ React.createElement(StandardListItem, {
            ...listItemProps,
            key: listItem.key
        }, listItem.children);
    }));
});
const StyledList = styled(UnstyledList)`
  ${baseListStyle}
  ${boxShadowLists}
  ${getListVirtualizedCss({
    borderWidth,
    height,
    padding
})}
  ${({ look })=>getListTheme({
        look
    })}
  min-width: 100px;

  &.bf-list__standard--virtualized {
    overflow: unset;
    padding: 0;
  }

  .bf-list__virtualized {
    > div {
      padding: ${padding}px ${padding}px 0;

      > div > div {
        &:last-child {
          padding-bottom: ${padding}px;
        }
      }
    }
  }
`;
/**
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/list_role
 *
 * @param props StandardListProps
 * @returns FunctionComponent<StandardListProps>
 */ const StandardList = /*#__PURE__*/ forwardRef((props, ref)=>{
    const { look = ListLooks.Default, ...otherProps } = props;
    const themeContext = useContext(ThemeContext);
    return /*#__PURE__*/ React.createElement(ThemeProvider, {
        theme: themeContext || lightTheme
    }, /*#__PURE__*/ React.createElement(StyledList, {
        ...otherProps,
        look: look,
        ref: ref
    }));
});

export { StandardList };
//# sourceMappingURL=standard-list.js.map
